import {
  ArrayControlProps,
  isControl,
  rankWith,
  resolveSchema,
} from "@jsonforms/core";
import { withJsonFormsArrayControlProps } from "@jsonforms/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Carousel, Icon, Loader } from "rsuite";
import { FileService } from "../../../services/FileService";
import "./AttachmentPreviewRenderer.scss";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isImageFile = (nmExtension: string) =>
  IMAGE_EXTENSIONS.includes(nmExtension?.toLowerCase());

const downloadFile = (item: AttachmentItem) => {
  const url = FileService.getFileURL(item.idExternalFile, "original", "");
  axios.get(url, { responseType: "blob" }).then((res) => {
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${item.nmFile}.${item.nmExtension}`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  });
};

interface AttachmentItem {
  nmFile: string;
  nmExtension: string;
  idExternalFile: string;
  nmUser: string;
  nrFileSize: number;
  dtIncluded: Date;
}

const AuthenticatedImage = ({
  src,
  alt,
  onClick,
}: {
  src: string;
  alt: string;
  onClick?: () => void;
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;

    axios
      .get(src, { responseType: "blob" })
      .then((res) => {
        if (revoked) return;
        setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {
        if (!revoked) setError(true);
      });

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (error) {
    return (
      <div className="file-placeholder">
        <Icon icon="image" size="4x" />
        <span className="file-name">Preview unavailable</span>
      </div>
    );
  }

  if (!blobUrl) {
    return <Loader size="md" />;
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className="preview-image"
      onClick={() => onClick?.()}
      style={{ cursor: "pointer" }}
    />
  );
};

const FullscreenPreview = ({
  items,
  initialIndex,
  onClose,
}: {
  items: AttachmentItem[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const currentItem = items[currentIndex];
  const isImage = isImageFile(currentItem.nmExtension);

  useEffect(() => {
    if (!isImage) {
      setBlobUrl(null);
      return;
    }

    let revoked = false;
    setBlobUrl(null);

    axios
      .get(FileService.getFileURL(currentItem.idExternalFile, "original", ""), {
        responseType: "blob",
      })
      .then((res) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {});

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev + 1) % items.length);
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, items.length]);

  return createPortal(
    <div className="AttachmentPreviewRenderer">
      <div className="fullscreen-overlay" onClick={onClose}>
        <button
          className="fullscreen-nav fullscreen-nav-left"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
          }}
        >
          <Icon icon="angle-left" size="3x" />
        </button>
        <div
          className="fullscreen-content"
          onClick={(e) => e.stopPropagation()}
        >
          {isImage ? (
            blobUrl ? (
              <>
                <img
                  src={blobUrl}
                  alt={currentItem.nmFile}
                  className="fullscreen-image"
                />
                <div className="fullscreen-caption">
                  {currentItem.nmFile}.{currentItem.nmExtension} (
                  {currentIndex + 1}/{items.length})
                </div>
              </>
            ) : (
              <Loader size="lg" />
            )
          ) : (
            <>
              <div className="fullscreen-no-preview">
                <Icon icon="file" size="4x" />
                <span>Preview not available for this file type</span>
              </div>
              <div className="fullscreen-caption">
                {currentItem.nmFile}.{currentItem.nmExtension} (
                {currentIndex + 1}/{items.length})
              </div>
            </>
          )}
        </div>
        <button
          className="fullscreen-nav fullscreen-nav-right"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev + 1) % items.length);
          }}
        >
          <Icon icon="angle-right" size="3x" />
        </button>
        <button className="fullscreen-close" onClick={onClose}>
          <Icon icon="close" size="2x" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

const AuthenticatedThumbnail = ({
  src,
  alt,
  onClick,
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;

    axios
      .get(src, { responseType: "blob" })
      .then((res) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {});

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <div className="thumbnail-item" onClick={onClick}>
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className="thumbnail-image" />
      ) : (
        <Loader size="xs" />
      )}
    </div>
  );
};

const AttachmentPreviewRenderer = (props: ArrayControlProps) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const goToSlide = (index: number) => {
    const inputs = carouselRef.current?.querySelectorAll<HTMLInputElement>(
      ".rs-carousel-label-wrapper input[type='radio']",
    );
    inputs?.[index]?.click();
  };

  if (!Array.isArray(props.data) || props.data.length === 0) {
    return (
      <div className="group-container">
        <div className="no-data">Nothing to display</div>
      </div>
    );
  }

  return (
    <div
      className="group-container AttachmentPreviewRenderer"
      ref={carouselRef}
    >
      {fullscreenIndex !== null && (
        <FullscreenPreview
          items={props.data}
          initialIndex={fullscreenIndex}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
      <Carousel autoplay placement="bottom" shape="dot">
        {props.data.map((item: AttachmentItem, index: number) => {
          const isImage = isImageFile(item.nmExtension);
          const previewUrl = FileService.getFileURL(
            item.idExternalFile,
            "preview",
            "",
          );

          return (
            <div key={item.idExternalFile ?? index} className="carousel-slide">
              <div className="slide-preview">
                {isImage ? (
                  <AuthenticatedImage
                    src={previewUrl}
                    alt={item.nmFile}
                    onClick={() => setFullscreenIndex(index)}
                  />
                ) : (
                  <div className="file-placeholder">
                    <Icon icon="file" size="4x" />
                  </div>
                )}
              </div>
              <div className="slide-details">
                <div className="detail-row">
                  <span className="detail-label">File Name</span>
                  <span className="detail-value">
                    {item.nmFile}.{item.nmExtension}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Size</span>
                  <span className="detail-value">
                    {formatBytes(item.nrFileSize)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Uploaded By</span>
                  <span className="detail-value">{item.nmUser}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    {formatDate(item.dtIncluded)}
                  </span>
                </div>
                <button
                  className="download-button"
                  onClick={() => downloadFile(item)}
                >
                  <Icon icon="download" /> Download
                </button>
              </div>
            </div>
          );
        })}
      </Carousel>
      <div className="thumbnail-strip">
        {props.data.map((item: AttachmentItem, index: number) => {
          const isImage = isImageFile(item.nmExtension);
          const previewUrl = FileService.getFileURL(
            item.idExternalFile,
            "preview",
            "",
          );

          return isImage ? (
            <AuthenticatedThumbnail
              key={item.idExternalFile ?? index}
              src={previewUrl}
              alt={item.nmFile}
              onClick={() => goToSlide(index)}
            />
          ) : (
            <div
              key={item.idExternalFile ?? index}
              className="thumbnail-item"
              onClick={() => goToSlide(index)}
            >
              <div className="thumbnail-placeholder">
                <Icon icon="file" size="lg" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const attachmentPreviewTester = rankWith(
  100,
  (uischema, schema, context) => {
    if (!isControl(uischema)) return false;
    if (uischema.options?.variant !== "attachmentPreview") return false;
    const resolvedSchema = resolveSchema(
      schema,
      uischema.scope,
      context?.rootSchema,
    );
    return resolvedSchema?.type === "array";
  },
);

export default withJsonFormsArrayControlProps(AttachmentPreviewRenderer);
